// "Ring until the popup is closed" for new booking requests (user.bookingAlertMode === "UNTIL_DISMISSED").
//
// Android: the native BookingAlertService rings - looping on the alarm stream, also with the app closed, where it is
// started by the backend's "booking_alert" push. The app only tells it which requests are still open.
// Web: the same rules with an <audio> loop while the tab is open.
//
// Rules (kept identical to BookingAlertService.java):
//  - a request rings from when it arrives until it is answered, its popup is closed, or its deadline passes;
//  - once one request is done, the others stay quiet for its repeat gap, then ring again if still pending;
//  - a brand-new request rings straight away, even inside a gap;
//  - a request that was stopped never rings again in the same response window (a re-request has a later deadline).

import { Capacitor, registerPlugin } from "@capacitor/core";
import { getSelectedSound, soundFileUrl, soundResourceName } from "@/lib/notification-sound";

export interface BookingAlarm {
  bookingId: string;
  title: string;
  body: string;
  /** Owner-response deadline, epoch ms. */
  deadline: number;
  repeatGapMs: number;
}

export interface AlarmDeviceStatus {
  notificationsEnabled: boolean;
  fullScreenAllowed: boolean;
  batteryUnrestricted: boolean;
}

interface BookingAlertPlugin {
  start(options: BookingAlarm & { sound: string }): Promise<void>;
  stop(options: { bookingId: string; deadline?: number }): Promise<void>;
  clear(): Promise<void>;
  getStatus(): Promise<AlarmDeviceStatus>;
  openFullScreenSettings(): Promise<void>;
  openNotificationSettings(): Promise<void>;
  requestBatteryUnrestricted(): Promise<void>;
}

/** Native side: android/app/src/main/java/com/darshan/pms/BookingAlertPlugin.java */
export const BookingAlertNative = registerPlugin<BookingAlertPlugin>("BookingAlert");

export const hasNativeBookingAlarm = () => Capacitor.getPlatform() === "android";

const SILENCE_FALLBACK_MS = 30 * 60_000;

class WebRinger {
  private alarms = new Map<string, BookingAlarm>();
  private silenced = new Map<string, number>();
  private quietUntil = 0;
  private timer: number | undefined;
  private audio: HTMLAudioElement | null = null;

  start(alarm: BookingAlarm) {
    if (alarm.deadline <= Date.now() || (this.silenced.get(alarm.bookingId) ?? 0) >= alarm.deadline) return;
    const isNew = !this.alarms.has(alarm.bookingId);
    this.alarms.set(alarm.bookingId, alarm);
    if (isNew) this.quietUntil = 0;
    this.reconcile();
  }

  stop(bookingId: string, deadline?: number) {
    const until = deadline && deadline > 0 ? deadline : Date.now() + SILENCE_FALLBACK_MS;
    this.silenced.set(bookingId, Math.max(until, this.silenced.get(bookingId) ?? 0));
    const removed = this.alarms.get(bookingId);
    if (removed) {
      this.alarms.delete(bookingId);
      this.startQuietGap(removed);
    }
    this.reconcile();
  }

  clear() {
    this.alarms.clear();
    this.silenced.clear();
    this.quietUntil = 0;
    this.reconcile();
  }

  private startQuietGap(finished: BookingAlarm) {
    if (this.alarms.size && finished.repeatGapMs > 0) this.quietUntil = Math.max(this.quietUntil, Date.now() + finished.repeatGapMs);
  }

  private reconcile() {
    window.clearTimeout(this.timer);
    const now = Date.now();
    for (const alarm of [...this.alarms.values()]) {
      if (alarm.deadline <= now) {
        this.alarms.delete(alarm.bookingId);
        this.startQuietGap(alarm);
      }
    }
    if (!this.alarms.size) {
      this.silence();
      return;
    }
    let nextCheck = Math.min(...[...this.alarms.values()].map((alarm) => alarm.deadline));
    if (now < this.quietUntil) {
      this.silence();
      nextCheck = Math.min(nextCheck, this.quietUntil);
    } else {
      this.ring();
    }
    this.timer = window.setTimeout(() => this.reconcile(), Math.max(250, nextCheck - now));
  }

  private ring() {
    if (typeof Audio === "undefined") return;
    const src = soundFileUrl(getSelectedSound());
    if (!this.audio) {
      this.audio = new Audio();
      this.audio.loop = true;
      this.audio.volume = 0.7;
    }
    if (this.audio.src.endsWith(src) && !this.audio.paused) return;
    this.audio.src = src;
    // Browsers block autoplay until the page has had a click; the popup is still shown either way.
    void this.audio.play().catch(() => undefined);
  }

  private silence() {
    if (!this.audio) return;
    this.audio.pause();
    this.audio.currentTime = 0;
  }
}

const webRinger = new WebRinger();

export const bookingAlarm = {
  start(alarm: BookingAlarm) {
    if (hasNativeBookingAlarm()) {
      void BookingAlertNative.start({ ...alarm, sound: soundResourceName(getSelectedSound()) }).catch(() => undefined);
    } else {
      webRinger.start(alarm);
    }
  },
  stop(bookingId: string, deadline?: number) {
    if (hasNativeBookingAlarm()) {
      void BookingAlertNative.stop({ bookingId, deadline }).catch(() => undefined);
    } else {
      webRinger.stop(bookingId, deadline);
    }
  },
  /** Logout / switching away from owner mode: drop everything, remember nothing. */
  clear() {
    if (hasNativeBookingAlarm()) {
      void BookingAlertNative.clear().catch(() => undefined);
    } else {
      webRinger.clear();
    }
  },
};
